import React, { FC, useCallback, useEffect, useState } from 'react'
import styles from 'styles/components/template.module.scss'
import { Spacer } from 'components/Utility'
import { CautionButton, GreyButton, NavButton, OrangeButton } from 'components/Button'
import { BreadType } from 'types/bread'
import { useDispatch, useSelector } from 'react-redux'
import { State } from 'index'
import { getSpecialHolidays } from 'reducks/calendar/selectors'
import { getStoreRef } from 'reducks/auth/selectors'
import { StockType } from 'types/stock'
import { getStocks } from 'reducks/stock/selectors'
import { CheckoutStatusType, OrderType } from 'types/checkout'
import { getOrders } from 'reducks/checkout/selectors'
import { DayOfWeekType, DaysOfWeek } from 'lib/daysOfWeek'
import { useSelectWeekday } from 'hooks/form'
import { addMonths, eachDayOfInterval, endOfMonth, isBefore, isSameDay, startOfMonth } from 'date-fns'
import { getBreadShortName, parseToDate } from 'lib/helper'
import { addDeliveryStocks, deleteDeliveryStocks, fetchFutureStocks } from 'reducks/stock/operations'
import { fetchBreadOrdersInPeriod } from 'reducks/checkout/operations'
import { CautionHeadline, Description, MainHeadline, SmallHeadline, SubDescription } from 'components/Typography'
import { Card, ScheduleCardReadonly, StoreDeliveryBreadCard } from 'components/Card'
import { ScheduleCalendarContainer } from 'components/Calendar'
import { BasicModal } from 'components/Modal'
import text from 'styles/utility/text.module.scss'
import { FlexRow } from 'components/Flex'
import { getStoreDeliveryBread } from 'reducks/storeDeliveryBread/selectors'
import { fetchStoreDeliveryBread } from 'reducks/storeDeliveryBread/operations'
import { initCalendar } from 'reducks/calendar/operations'
import { useIndividualDeliveryDates } from 'hooks/stock'
const daysOfWeekInstance = new DaysOfWeek()
const allDaysOfWeeks = daysOfWeekInstance.getDaysOfWeek()

type Props = {
  bread: BreadType
}

const DeliveryScheduleEditTemplate: FC<Props> = (props) => {
  const { bread } = props
  const type = 'delivery'
  const dispatch = useDispatch()
  const storeRef = useSelector((state: State) => getStoreRef(state))
  const specialHolidays = useSelector((state: State) => getSpecialHolidays(state))
  const stocks: StockType[] = useSelector((state: State) => getStocks(state))
  const orders: OrderType[] = useSelector((state: State) => getOrders(state))
  const storeDeliveryBread = useSelector((state: State) => getStoreDeliveryBread(state))

  const [activeDates, setActiveDates] = useState<Date[]>([])
  const [currentMonth, setCurrentMonth] = useState<Date>(new Date())
  const [openCaution, setOpenCaution] = useState<boolean>(false)
  const [isEdited, setIsEdited] = useState<boolean>(false)
  const [individualSettingDates, setIndividualSettingDates] = useState<Date[]>([])
  const [daysOfWeek, setDaysOfWeek] = useState<DayOfWeekType[]>([])

  const handleCurrentMonth = useCallback((month: Date) => setCurrentMonth(month), [setCurrentMonth])
  const openCautionModal = useCallback(() => setOpenCaution(true), [setOpenCaution])
  const closeCautionModal = useCallback(() => setOpenCaution(false), [setOpenCaution])
  const selectDaysOfWeek = useSelectWeekday(setDaysOfWeek)
  const selectAllDaysOfWeek = useCallback(() => setDaysOfWeek([...allDaysOfWeeks]), [setDaysOfWeek])
  const unselectAllDaysOfWeek = useCallback(() => setDaysOfWeek([]), [setDaysOfWeek])

  const selectDate = useCallback(
    (date: Date) => {
      // 過去の日付を追加しない、当日は追加できる
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) return

      setActiveDates((prevState) => {
        if (prevState.some((activeDate) => isSameDay(activeDate, date))) {
          return prevState.filter((activeDate) => !isSameDay(activeDate, date))
        } else {
          return [...prevState, date]
        }
      })

      setIndividualSettingDates((prevState) => {
        // 個別受付日を解除した場合
        if (prevState.some((individualSettingDate) => isSameDay(individualSettingDate, date))) {
          return prevState.filter((individualSettingDate) => !isSameDay(individualSettingDate, date))
        } else {
          return prevState
        }
      })
      setIsEdited(true)
    },
    [setActiveDates, setIsEdited]
  )

  // 選択した曜日で一括設定（上書き）する
  const selectAllDateByDaysOfWeek = useCallback(() => {
    // 当月の日付を全て取得する
    const datesInMonth = eachDayOfInterval({
      start: startOfMonth(currentMonth),
      end: endOfMonth(currentMonth),
    })

    const dates: Date[] = []

    for (let i = 0; i < datesInMonth.length; i = (i + 1) | 0) {
      const date = datesInMonth[i]
      // 過去の日付であれば除外
      if (isBefore(date, new Date()) && !isSameDay(date, new Date())) continue
      // 選択されていない曜日は除外
      if (!daysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(date, specialHolidays))) continue
      dates.push(date)
    }

    // 当月の受付設定を一括更新
    setActiveDates((prevState) => {
      const activeDatesInOtherMonths = prevState.filter((activeDate) => {
        return datesInMonth.every((date) => !isSameDay(date, activeDate))
      })
      return [...activeDatesInOtherMonths, ...dates]
    })

    // 当月の個別設定を一括更新
    setIndividualSettingDates((prevState) => {
      // 他の月、休業日、受渡可能日以外の個別設定日を除外
      const remainingIndividualSettingDates = prevState.filter((individualSettingDate) => {
        return datesInMonth.every((date) => !isSameDay(date, individualSettingDate))
      })
      // 選択されていない曜日だが注文のある日は個別設定日に追加
      const orderedDates = datesInMonth.filter(
        (date) =>
          !daysOfWeek.includes(daysOfWeekInstance.getDayOfWeek(date, specialHolidays)) &&
          orders.some((order) => isSameDay(date, parseToDate(order.date)))
      )
      return [...remainingIndividualSettingDates, ...orderedDates]
    })

    setDaysOfWeek([])
    setIsEdited(true)
  }, [
    daysOfWeek,
    currentMonth,
    specialHolidays,
    orders,
    setActiveDates,
    setIndividualSettingDates,
    setDaysOfWeek,
    setIsEdited,
  ])

  useEffect(() => {
    if (!bread || !bread.bread_ref) return
    dispatch(fetchStoreDeliveryBread(storeRef, bread.bread_ref))
  }, [bread, storeRef])

  // 事前予約商品のstocksを取得
  useEffect(() => {
    if (!bread || !bread.bread_ref || !storeRef) return
    dispatch(fetchFutureStocks({ storeRef, types: [type], breadRef: bread.bread_ref }))
  }, [bread, storeRef, type])

  // お届け可能日に設定済みの日付を取得
  useEffect(() => {
    const dates = stocks.map((stock) => parseToDate(stock.date))
    setActiveDates([...dates])
  }, [stocks])

  // 注文情報を取得する
  useEffect(() => {
    if (!bread || !bread.bread_ref || !storeRef) return
    const status: CheckoutStatusType[] = ['succeeded', 'requires_capture']
    const start = startOfMonth(new Date())
    const end = endOfMonth(addMonths(currentMonth, 1))
    dispatch(fetchBreadOrdersInPeriod(start, end, storeRef, bread.bread_ref, status, type))
  }, [currentMonth, bread, storeRef, type])

  // 休業日を初期化する
  useEffect(() => {
    dispatch(initCalendar())
  }, [])

  // 個別で設定されている日付かを判定
  useIndividualDeliveryDates(storeDeliveryBread, stocks, setIndividualSettingDates)

  return (
    <div className={styles.standard}>
      <Spacer size={28} />
      <SmallHeadline label={'対象の商品'} />
      <Spacer size={8} />
      <Card>
        <ScheduleCardReadonly
          breadName={getBreadShortName(bread)}
          imageUrl={bread.image_urls[0] || ''}
          hasStocks={stocks.length > 0}
        />
      </Card>
      <Spacer size={48} />
      <MainHeadline headline={'お届け可能日の登録'} />
      <Spacer size={28} />
      <SmallHeadline label={'お届け可能日'} required={true} />
      <Spacer size={8} />
      <SubDescription description={'お客さまにお届けできる日をタップして選択してください。'} />
      <Spacer size={12} />
      <ScheduleCalendarContainer
        activeDates={activeDates}
        businessDays={allDaysOfWeeks}
        currentMonth={currentMonth}
        daysOfWeek={daysOfWeek}
        handleCurrentMonth={handleCurrentMonth}
        individualSettingDates={individualSettingDates}
        selectAllDateByDaysOfWeek={selectAllDateByDaysOfWeek}
        selectAllDaysOfWeek={selectAllDaysOfWeek}
        selectDate={selectDate}
        selectDaysOfWeek={selectDaysOfWeek}
        unselectAllDaysOfWeek={unselectAllDaysOfWeek}
      />
      <Spacer size={48} />
      <OrangeButton
        disabled={!isEdited || activeDates.length === 0}
        label={'設定を保存する'}
        onClick={() => {
          dispatch(addDeliveryStocks(activeDates, bread, orders, stocks, individualSettingDates, storeDeliveryBread))
        }}
        size={'medium'}
        type={'main'}
      />
      {stocks.length > 0 && (
        <>
          <Spacer size={12} />
          <NavButton
            label={'お届け可能日を削除する'}
            iconPath={'/icon/grey/delete.svg'}
            onClick={openCautionModal}
            url={''}
          />
          <BasicModal open={openCaution} closeModal={closeCautionModal}>
            <CautionHeadline caution={'お届け可能日を削除します'} />
            <Spacer size={12} />
            <Description description={'一度削除した設定は、取り消すことが出来ませんがよろしいですか？'} />
            <p className={text.color_caution}>※すでにお客さまから受けている注文は、取り消すことはできません</p>
            <Spacer size={16} />
            <FlexRow justifyContent={'center'}>
              <GreyButton label={'キャンセル'} onClick={closeCautionModal} size={'small'} type={'outlined'} />
              <Spacer size={16} />
              <CautionButton
                label={'削除する'}
                onClick={() => {
                  dispatch(deleteDeliveryStocks(bread, stocks, orders))
                }}
                size={'small'}
              />
            </FlexRow>
          </BasicModal>
        </>
      )}
      <Spacer size={48} />
      <MainHeadline headline={'商品設定情報'} />
      <Spacer size={28} />
      <StoreDeliveryBreadCard storeDeliveryBread={storeDeliveryBread} />
      <Spacer size={48} />
      <NavButton label={'お届け可能日設定に戻る'} iconPath={'/icon/grey/arrow_prev.svg'} url={'/schedule/delivery'} />
      <Spacer size={12} />
      <NavButton label={'トップページに戻る'} iconPath={'/icon/grey/home.svg'} url={'/'} />
      <Spacer size={72} />
    </div>
  )
}

export default DeliveryScheduleEditTemplate
